import { router, usePathname } from 'expo-router';
import * as React from 'react';

import { useSession } from '@/components/session-provider';
import { useCurrentTime } from '@/lib/dev-clock';
import { fetchLiveCheckIn, type LiveCheckIn, type LiveCheckInEvent } from '@/lib/event-checkins';
import { isEventUnderway } from '@sixam/shared/event-time';

const LIVE_REFRESH_MS = 30_000;

type UserLiveCheckIn = Readonly<{
  live: LiveCheckIn | null;
  userId: string;
}>;

type LiveCheckInContextValue = Readonly<{
  operatorEvent: LiveCheckInEvent | null;
}>;

const LiveCheckInContext = React.createContext<LiveCheckInContextValue>({
  operatorEvent: null,
});

/**
 * Who has already been opened onto their own QR. Operators deliberately do not
 * participate in this guard: their flow is the persistent one-tap shortcut.
 */
let autoOpenedAttendeeUserId: string | null = null;

function isCheckInRoute(pathname: string): boolean {
  return pathname.startsWith('/admin/check-in') || pathname.startsWith('/app/check-in');
}

function liveCheckInForUser(
  result: UserLiveCheckIn | null,
  userId: string | null
): LiveCheckIn | null {
  if (!result || !userId || result.userId !== userId) {
    return null;
  }
  return result.live;
}

function debug(...parts: readonly unknown[]) {
  if (__DEV__) {
    console.log('[live check-in entry]', ...parts);
  }
}

export function openOperatorCheckIn(eventId: string) {
  router.push({
    params: { id: eventId },
    pathname: '/admin/check-in/[id]',
  });
}

export function useActiveOperatorCheckIn(): LiveCheckInEvent | null {
  return React.use(LiveCheckInContext).operatorEvent;
}

/**
 * One source of truth for how a live event enters the signed-in experience:
 *
 * - headers can expose the active operator action without overlaying content;
 * - registered runners still open directly onto their own QR;
 * - tapping the operator action opens the camera itself, not an intermediate
 *   event details screen.
 */
export function LiveCheckInProvider({ children }: Readonly<React.PropsWithChildren>) {
  const { session } = useSession();
  const pathname = usePathname();
  const now = useCurrentTime();
  const [result, setResult] = React.useState<UserLiveCheckIn | null>(null);
  const requestIdRef = React.useRef(0);
  const userId = session?.user.id ?? null;
  const live = liveCheckInForUser(result, userId);

  React.useEffect(() => {
    if (!userId) {
      return undefined;
    }
    const activeUserId = userId;

    let active = true;

    function loadLiveCheckIn() {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      void fetchLiveCheckIn()
        .then((nextLive) => {
          if (active && requestId === requestIdRef.current) {
            setResult({ live: nextLive, userId: activeUserId });
            debug('server said', nextLive ? `${nextLive.role}: ${nextLive.event.title}` : 'none');
          }
        })
        .catch((error: unknown) => {
          if (active && requestId === requestIdRef.current) {
            debug('lookup failed:', error instanceof Error ? error.message : error);
          }
        });
    }

    loadLiveCheckIn();
    const interval = setInterval(loadLiveCheckIn, LIVE_REFRESH_MS);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [pathname, userId]);

  React.useEffect(() => {
    if (
      !userId ||
      live?.role !== 'attendee' ||
      autoOpenedAttendeeUserId === userId ||
      isCheckInRoute(pathname)
    ) {
      return;
    }

    autoOpenedAttendeeUserId = userId;
    router.replace({
      params: { auto: '1', id: live.event.id },
      pathname: '/app/check-in/[id]',
    });
  }, [live, pathname, userId]);

  const operatorEvent =
    live?.role === 'operator' && isEventUnderway(live.event, now) && !isCheckInRoute(pathname)
      ? live.event
      : null;
  const value = React.useMemo<LiveCheckInContextValue>(() => ({ operatorEvent }), [operatorEvent]);

  return <LiveCheckInContext.Provider value={value}>{children}</LiveCheckInContext.Provider>;
}
